import React, { useState, useRef, useCallback } from 'react';
import {
  CloudArrowUpIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  DocumentIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import Button from '../ui/Button';
import Card, { CardContent } from '../ui/Card';

interface FileUploadProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  maxFiles?: number;
  onUpload: (files: File[]) => Promise<void>;
  onRemove?: (fileId: string) => void;
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  disabled?: boolean;
  className?: string;
  showPreview?: boolean;
  dragText?: string;
  dropText?: string;
}

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  url?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  accept = '*/*',
  multiple = false,
  maxSize = 10,
  maxFiles = 5,
  onUpload,
  onRemove,
  value = [],
  onChange,
  disabled = false,
  className = '',
  showPreview = true,
  dragText = 'Drag and drop files here',
  dropText = 'or click to browse',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(value);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <PhotoIcon className="w-6 h-6 text-green-500" />;
    } else if (file.type.startsWith('video/')) {
      return <VideoCameraIcon className="w-6 h-6 text-blue-500" />;
    } else if (file.type.startsWith('audio/')) {
      return <MusicalNoteIcon className="w-6 h-6 text-purple-500" />;
    } else {
      return <DocumentIcon className="w-6 h-6 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size exceeds ${maxSize}MB limit`;
    }

    // Check file type if accept is specified
    if (accept !== '*/*') {
      const acceptedTypes = accept.split(',').map(type => type.trim());
      const isAccepted = acceptedTypes.some(acceptedType => {
        if (acceptedType.startsWith('.')) {
          // Extension check
          return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
        } else {
          // MIME type check
          return file.type.match(acceptedType.replace('*', '.*'));
        }
      });

      if (!isAccepted) {
        return `File type ${file.type} is not accepted`;
      }
    }

    return null;
  };

  const createPreview = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadeddata = () => {
          video.currentTime = 1;
        };
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL());
        };
        video.src = URL.createObjectURL(file);
      } else {
        resolve(''); // No preview for non-media files
      }
    });
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);

    // Check max files limit
    if (!multiple && fileArray.length > 1) {
      alert('Only one file is allowed');
      return;
    }

    if (uploadedFiles.length + fileArray.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const newFiles: UploadedFile[] = [];

    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        alert(`${file.name}: ${validationError}`);
        continue;
      }

      const fileId = generateFileId();
      const preview = showPreview ? await createPreview(file) : undefined;

      newFiles.push({
        id: fileId,
        file,
        preview,
        progress: 0,
        status: 'pending',
      });
    }

    const updatedFiles = [...uploadedFiles, ...newFiles];
    setUploadedFiles(updatedFiles);
    onChange?.(updatedFiles);

    // Start upload process
    setIsUploading(true);
    try {
      await onUpload(newFiles.map(f => f.file));

      // Mark all as successful
      const successfulFiles = updatedFiles.map(f => ({
        ...f,
        status: 'success' as const,
        progress: 100,
      }));
      setUploadedFiles(successfulFiles);
      onChange?.(successfulFiles);
    } catch (error) {
      // Mark all as failed
      const failedFiles = updatedFiles.map(f => ({
        ...f,
        status: 'error' as const,
        error: error instanceof Error ? error.message : 'Upload failed',
      }));
      setUploadedFiles(failedFiles);
      onChange?.(failedFiles);
    } finally {
      setIsUploading(false);
    }
  }, [uploadedFiles, maxFiles, maxSize, multiple, onUpload, onChange, showPreview, accept]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!disabled && e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && !disabled) {
      handleFiles(e.target.files);
    }
  };

  const openFileDialog = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const removeFile = (fileId: string) => {
    const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
    setUploadedFiles(updatedFiles);
    onChange?.(updatedFiles);
    onRemove?.(fileId);
  };

  const retryUpload = async (fileId: string) => {
    const file = uploadedFiles.find(f => f.id === fileId);
    if (!file) return;

    // Update status to uploading
    const updatedFiles = uploadedFiles.map(f =>
      f.id === fileId
        ? { ...f, status: 'uploading' as const, error: undefined }
        : f
    );
    setUploadedFiles(updatedFiles);
    onChange?.(updatedFiles);

    try {
      await onUpload([file.file]);

      // Mark as successful
      const successfulFiles = updatedFiles.map(f =>
        f.id === fileId
          ? { ...f, status: 'success' as const, progress: 100 }
          : f
      );
      setUploadedFiles(successfulFiles);
      onChange?.(successfulFiles);
    } catch (error) {
      // Mark as failed again
      const failedFiles = updatedFiles.map(f =>
        f.id === fileId
          ? { ...f, status: 'error' as const, error: error instanceof Error ? error.message : 'Upload failed' }
          : f
      );
      setUploadedFiles(failedFiles);
      onChange?.(failedFiles);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg transition-colors duration-200
          ${isDragOver ? 'border-orange-500 bg-orange-50' : 'border-gray-300 bg-gray-50'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileInput}
          disabled={disabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        <CardContent className="p-8 text-center">
          <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">{dragText}</p>
          <p className="text-sm text-gray-600 mb-4">{dropText}</p>

          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
          >
            Choose Files
          </Button>

          <div className="mt-4 text-xs text-gray-500">
            {accept !== '*/*' && <p>Accepted formats: {accept}</p>}
            <p>Maximum file size: {maxSize}MB</p>
            {multiple && <p>Maximum files: {maxFiles}</p>}
          </div>
        </CardContent>
      </div>

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <div className="mt-4 space-y-3">
          {uploadedFiles.map((uploadedFile) => (
            <Card key={uploadedFile.id} className="border border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  {/* File Preview/Icon */}
                  <div className="flex-shrink-0">
                    {uploadedFile.preview && uploadedFile.file.type.startsWith('image/') ? (
                      <img
                        src={uploadedFile.preview}
                        alt={uploadedFile.file.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        {getFileIcon(uploadedFile.file)}
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {uploadedFile.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(uploadedFile.file.size)}
                    </p>

                    {/* Progress Bar */}
                    {uploadedFile.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-orange-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${uploadedFile.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Uploading... {uploadedFile.progress}%
                        </p>
                      </div>
                    )}

                    {/* Status Messages */}
                    {uploadedFile.status === 'success' && (
                      <div className="flex items-center text-xs text-green-600 mt-1">
                        <CheckCircleSolidIcon className="w-4 h-4 mr-1" />
                        Upload complete
                      </div>
                    )}

                    {uploadedFile.status === 'error' && (
                      <div className="text-xs text-red-600 mt-1">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        {uploadedFile.error || 'Upload failed'}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {uploadedFile.status === 'error' && (
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => retryUpload(uploadedFile.id)}
                        disabled={isUploading}
                      >
                        Retry
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => removeFile(uploadedFile.id)}
                      disabled={isUploading}
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Overall Upload Status */}
      {isUploading && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-sm text-blue-800">
              Uploading files... Please don't close this page.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
